import React, { useState, useEffect } from 'react';
import { Send, Award } from 'lucide-react';
import questionsData from '../../components/ai_questions.json';

const DynamicQuestionnaire = () => {
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [scoringResult, setScoringResult] = useState(null);

  useEffect(() => {
    const flattenQuestions = (data, parentPath = "") => {
      const questions = [];
      let questionId = 1;

      const traverse = (obj, path = "") => {
        Object.entries(obj).forEach(([key, value]) => {
          const currentPath = path ? `${path} > ${key}` : key;

          if (Array.isArray(value)) {
            value.forEach(question => {
              questions.push({
                id: questionId++,
                question: typeof question === 'string' ? question : question.question,
                category: currentPath,
                section: path.split(' > ').slice(-1)[0] || key,
                fullPath: currentPath,
                required: true,
                criticality: typeof question === 'object' ? question.criticality : 'Medium',
                score_yes: typeof question === 'object' ? question.score_yes : 5,
                score_no: typeof question === 'object' ? question.score_no : 0
              });
            });
          } else if (typeof value === 'object' && value !== null) {
            traverse(value, currentPath);
          }
        });
      };

      traverse(data);
      return questions;
    };

    const loadQuestions = () => {
      try {
        let questionsToUse;
        
        if (Array.isArray(questionsData)) {
          // Direct array format
          questionsToUse = questionsData.map((item, index) => ({
            id: index + 1,
            question: item.question,
            criticality: item.criticality || 'Medium',
            score_yes: item.score_yes || 0,
            score_no: item.score_no || 0,
            category: item.category || 'General',
            section: item.section || 'Default',
            fullPath: item.fullPath || item.category || 'General',
            required: true
          }));
        } else {
          // Nested format
          questionsToUse = flattenQuestions(questionsData);
        }

        setQuestions(questionsToUse);

        // Initialize responses
        const initialResponses = {};
        questionsToUse.forEach((q) => {
          initialResponses[q.id] = {
            answer: null,
            remarks: ''
          };
        });
        setResponses(initialResponses);

      } catch (error) {
        console.error('Error loading questions:', error);
        setSubmitStatus({
          type: 'error',
          message: 'Error loading questions from file.'
        });
      }
    };

    loadQuestions();
  }, []);


  const handleAnswerChange = (questionId, answer) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        answer: answer
      }
    }));
  };


  const calculateScore = () => {
    let totalScore = 0;
    let maxPossibleScore = 0;
    const categoryScores = {};

    questions.forEach(question => {
      const response = responses[question.id];
      const category = question.category || 'General';
      
      if (!categoryScores[category]) {
        categoryScores[category] = {
          score: 0,
          maxScore: 0,
          answered: 0,
          total: 0
        };
      }

      categoryScores[category].total++;
      
      if (response && response.answer !== null) {
        const score = response.answer ? question.score_yes : question.score_no;
        totalScore += score;
        categoryScores[category].score += score;
        categoryScores[category].answered++;
      }
      
      maxPossibleScore += Math.max(question.score_yes, question.score_no);
      categoryScores[category].maxScore += Math.max(question.score_yes, question.score_no);
    });

    const percentage = maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0;
    
    return {
      totalScore,
      maxPossibleScore,
      percentage,
      categoryScores
    };
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-400';
    if (percentage >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreLabel = (percentage) => {
    if (percentage >= 80) return 'Excellent';
    if (percentage >= 60) return 'Good';
    if (percentage >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmitStatus(null);

      const unansweredRequired = questions.filter(q => 
        q.required && (responses[q.id]?.answer === null || responses[q.id]?.answer === undefined)
      );

      if (unansweredRequired.length > 0) {
        setSubmitStatus({
          type: 'error',
          message: `Please answer all required questions. Missing: ${unansweredRequired.length} question(s)`
        });
        setIsSubmitting(false);
        return;
      }

      // Calculate scoring
      const scoringResult = calculateScore();
      
      const submissionData = {
        timestamp: new Date().toISOString(),
        responses: responses,
        totalQuestions: questions.length,
        completedQuestions: Object.values(responses).filter(r => r.answer !== null).length,
        scoring: scoringResult
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setScoringResult(scoringResult);
      setSubmitStatus({
        type: 'success',
        message: 'Assessment completed successfully!'
      });

      // Scroll to top to show results
      window.scrollTo({ top: 0, behavior: 'smooth' });

      console.log('Submission data:', submissionData);

    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      setSubmitStatus({
        type: 'error',
        message: `Submission failed: ${error.message || 'Unknown error'}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCompletionPercentage = () => {
    const totalQuestions = questions.length;
    const answeredQuestions = Object.values(responses).filter(r => r.answer !== null).length;
    return totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;
  };

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-transparent text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-8">AI Framework Review</h1>
            <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Loading questions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent text-white">
      {/* Sticky Progress Bar */}
      <div className="sticky top-0 z-50 bg-gray-900 border-b border-gray-700 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-white">AI Framework Review</h1>
              <p className="text-sm text-gray-400">
                Assessment Questions • {Object.values(responses).filter(r => r.answer !== null).length} of {questions.length} questions answered
              </p>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-blue-400">
                {getCompletionPercentage()}%
              </div>
              <div className="text-xs text-gray-400">Complete</div>
            </div>
          </div>
          
          <div className="w-full bg-gray-800 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${getCompletionPercentage()}%` }}
            ></div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          {/* Scoring Result */}
          

          {/* Questions */}
          <div className="space-y-6">
            {questions.map((question, index) => (
              <div key={question.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-blue-400 font-semibold">Q{index + 1}</span>
                      {question.required && (
                        <span className="text-red-400 text-sm">*</span>
                      )}
                      <span className="text-xs bg-blue-900 px-2 py-1 rounded text-blue-200">
                        {question.section || question.category}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        question.criticality === 'High' ? 'bg-red-900 text-red-200' :
                        question.criticality === 'Medium' ? 'bg-yellow-900 text-yellow-200' :
                        'bg-green-900 text-green-200'
                      }`}>
                        {question.criticality}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mb-2 font-mono">
                      {question.fullPath}
                    </div>
                    <h3 className="text-lg font-medium text-white mb-4">
                      {question.question}
                    </h3>
                  </div>
                </div>

                {/* Yes/No Options */}
                <div className="mb-4">
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleAnswerChange(question.id, true)}
                      className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex-1 ${
                        responses[question.id]?.answer === true
                          ? 'bg-blue-600 text-white border-2 border-blue-400'
                          : 'bg-gray-700 text-gray-300 border-2 border-gray-600 hover:bg-gray-600'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => handleAnswerChange(question.id, false)}
                      className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex-1 ${
                        responses[question.id]?.answer === false
                          ? 'bg-blue-600 text-white border-2 border-blue-400'
                          : 'bg-gray-700 text-gray-300 border-2 border-gray-600 hover:bg-gray-600'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>

                {/* Remarks Section */}
                {/* <div className="mt-4">
                  <button
                    onClick={() => toggleRemarks(question.id)}
                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors mb-3"
                  >
                    <FileText size={16} />
                    <span>Remarks</span>
                    {expandedRemarks[question.id] ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>

                  {expandedRemarks[question.id] && (
                    <div className="transition-all duration-300">
                      <textarea
                        value={responses[question.id]?.remarks || ''}
                        onChange={(e) => handleRemarksChange(question.id, e.target.value)}
                        placeholder="Add your remarks here..."
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical min-h-[100px]"
                      />
                    </div>
                  )}
                </div> */}
              </div>
            ))}
          </div>

          {/* Submit Section */}
          <div className="mt-8 p-6 bg-gray-800 rounded-lg border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-white mb-1">Ready to Submit?</h3>
                <p className="text-gray-400 text-sm">
                  {Object.values(responses).filter(r => r.answer !== null).length} of {questions.length} questions answered
                </p>
              </div>
              
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                  isSubmitting
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Calculating...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Submit Assessment
                  </>
                )}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus && (
              <div className={`mt-4 p-3 rounded-lg ${
                submitStatus.type === 'success' 
                  ? 'bg-green-900 border border-green-700 text-green-200' 
                  : 'bg-red-900 border border-red-700 text-red-200'
              }`}>
                {submitStatus.message}
              </div>
            )}
          </div>
          {scoringResult && (
            <div className="mb-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <Award className="text-yellow-400" size={24} />
                <h2 className="text-xl font-bold">Assessment Results</h2>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className={`text-3xl font-bold ${getScoreColor(scoringResult.percentage)}`}>
                    {scoringResult.percentage}%
                  </div>
                  <div className="text-gray-400 text-sm">Overall Score</div>
                  <div className={`text-sm font-medium ${getScoreColor(scoringResult.percentage)}`}>
                    {getScoreLabel(scoringResult.percentage)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">
                    {scoringResult.totalScore}
                  </div>
                  <div className="text-gray-400 text-sm">Points Earned</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-300">
                    {scoringResult.maxPossibleScore}
                  </div>
                  <div className="text-gray-400 text-sm">Max Possible</div>
                </div>
              </div>

              {/* Category Breakdown */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Category Breakdown</h3>
                <div className="space-y-2">
                  {Object.entries(scoringResult.categoryScores).map(([category, data]) => {
                    const categoryPercentage = data.maxScore > 0 ? Math.round((data.score / data.maxScore) * 100) : 0;
                    return (
                      <div key={category} className="flex items-center justify-between p-3 bg-gray-700 rounded">
                        <span className="font-medium">{category}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-400">
                            {data.score}/{data.maxScore}
                          </span>
                          <span className={`font-bold ${getScoreColor(categoryPercentage)}`}>
                            {categoryPercentage}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DynamicQuestionnaire;